// useSetApplicationTags.ts
import { useRef } from "react";
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL!;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY!;
const FN_URL = `${SUPABASE_URL}/functions/v1/set-application-tags`;

export type SetApplicationTagsArgs = {
  vacancyId: string;
  contactId: string;
  tags: string[];
  companyId?: string | null;
  actorEmail?: string | null;
  actorName?: string | null;
  historyContext?: {
    candidateName?: string | null;
    candidateEmail?: string | null;
    candidatePhone?: string | null;
    vacancyName?: string | null;
    vacancyTitle?: string | null;
    vacancyLocation?: string | null;
    vacancyDiscipline?: string | null;
    companyName?: string | null;
  };
};

export function useSetApplicationTags() {
  const inflightRef = useRef<Set<string>>(new Set());
  const getAccessToken = useAuth0AccessToken();

  async function setApplicationTags({
    vacancyId,
    contactId,
    tags,
    companyId,
    actorEmail,
    actorName,
    historyContext,
  }: SetApplicationTagsArgs) {
    const key = `${vacancyId}:${contactId}:tags`;
    if (inflightRef.current.has(key)) return;
    inflightRef.current.add(key);
    try {
      const accessToken = await getAccessToken();
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          apikey: SUPABASE_ANON,
          Authorization: `Bearer ${SUPABASE_ANON}`,
          "x-auth-token": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          vacancyId,
          contactId,
          tags,
          companyId,
          actorEmail,
          actorName,
          historyContext,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`);
      return json;
    } finally {
      inflightRef.current.delete(key);
    }
  }

  return { setApplicationTags };
}
